/**
 * Every request to our API says which client and version it comes from (X-App-Platform, X-App-Version), so the API
 * can answer an outdated client with 426 Upgrade Required (Sol forced-update P2 B). Done once, around fetch, so the
 * many places that call the API are all covered; other hosts are untouched. A 426 tells the update gate.
 */
let installed = false;
const listeners = new Set();

export function onUpgradeRequired(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(detail) {
  listeners.forEach(listener => {
    try {
      listener(detail);
    } catch (_) {
      // A listener's failure must not break the request that triggered it.
    }
  });
}

// Headers as a plain object from a Headers instance, a [name, value] list or an object.
function plain(headers) {
  const out = {};
  if (!headers) return out;
  if (typeof headers.forEach === 'function' && !Array.isArray(headers)) {
    headers.forEach((value, name) => {
      out[name] = value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([name, value]) => {
      out[name] = value;
    });
  } else {
    Object.assign(out, headers);
  }
  return out;
}

// A Request's own headers (Authorization among them) and the call's headers are kept; ours are only added.
function withHeaders(input, init, extra) {
  const headers = {...plain(typeof input !== 'string' && input ? input.headers : null), ...plain(init && init.headers)};
  const present = new Set(Object.keys(headers).map(name => name.toLowerCase()));
  Object.entries(extra).forEach(([name, value]) => {
    if (value && !present.has(name.toLowerCase())) headers[name] = value;
  });
  return {...(init || {}), headers};
}

export function installApiVersionHeaders({baseUrl, platform, version, target = globalThis}) {
  if (installed || !baseUrl || typeof target.fetch !== 'function') return false;
  const original = target.fetch.bind(target);
  target.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input && input.url;
    if (!url || !String(url).startsWith(baseUrl)) return original(input, init);
    const extra = {'X-App-Platform': platform, 'X-App-Version': version};
    const response = await original(input, withHeaders(input, init, extra));
    if (response && response.status === 426) {
      response
        .clone()
        .json()
        .then(body => notify({...((body && body.data) || {}), message: body && body.message}))
        .catch(() => notify({}));
    }
    return response;
  };
  installed = true;
  return true;
}

export function resetApiVersionHeadersForTests() {
  installed = false;
  listeners.clear();
}
